import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BadgeCheck, Instagram, Globe, MapPin, Flag, ArrowLeft, ExternalLink } from "lucide-react";
import { api, setPageMeta, setJsonLd, SITE_BASE } from "@/lib/api";
import { SaveClubButton } from "@/components/SaveClubButton";
import { CheckinButton } from "@/components/CheckinButton";
import { ClubRow } from "@/components/ClubRow";
import { VideoTile } from "@/components/VideoTile";
import { Reveal } from "@/components/Motion";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ChecklistMagnet } from "@/components/ChecklistMagnet";

export default function ClubProfile() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api.get(`/clubs/${slug}`)
      .then((r) => {
        setData(r.data);
        const c = r.data.club;
        const days = (c.meet_days || []).join(", ");
        setPageMeta(
          c.seo?.title ||
            (c.is_national
              ? `${c.name} — National Run Club: Chapters & Meetups | NRC`
              : `${c.name} — ${c.city} Run Club Schedule & Meetups | NRC`),
          c.seo?.description ||
            `${c.name} is a ${((c.tags || [])[0] || "community").toLowerCase()} run club${c.city ? ` in ${c.city}${c.state ? `, ${c.state}` : ""}` : ""}.${days ? ` Meets ${days}.` : ""} Paces, meetup details and how to join — human-verified by Ninja Run Club.`,
          c.logo ? { image: `${SITE_BASE}${c.logo.src}`, imageAlt: c.logo.alt || `${c.name} logo` } : {}
        );
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!data) return;
    const c = data.club;
    const sameAs = [...new Set([
      c.instagram && `https://instagram.com/${c.instagram}`,
      c.website,
      ...(c.links || []).map((l) => l.href),
    ].filter(Boolean))];
    return setJsonLd("club-jsonld", {
      "@context": "https://schema.org",
      "@type": "SportsClub",
      name: c.name,
      sport: "Running",
      url: `${SITE_BASE}/clubs/${c.slug}`,
      description: c.description,
      ...(sameAs.length ? { sameAs } : {}),
      ...(c.logo ? { logo: `${SITE_BASE}${c.logo.src}`, image: `${SITE_BASE}${c.logo.src}` } : {}),
      ...(c.schema?.founder ? { founder: { "@type": "Person", name: c.schema.founder } } : {}),
      ...(c.schema?.founding_date ? { foundingDate: c.schema.founding_date } : {}),
      ...(c.schema?.area_served ? { areaServed: c.schema.area_served } : {}),
      ...(c.city ? { address: { "@type": "PostalAddress", addressLocality: c.city, ...(c.state ? { addressRegion: c.state } : {}), addressCountry: "US" } } : {}),
    });
  }, [data]);

  useEffect(() => {
    const faqs = data?.club?.faqs;
    if (!faqs?.length) return;
    return setJsonLd("club-faq-jsonld", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }, [data]);

  useEffect(() => {
    if (notFound) setPageMeta("Club Not Found | Ninja Run Club", "", { noindex: true });
  }, [notFound]);

  const guideFacts = useMemo(() => {
    const g = data?.club?.guide;
    if (!g) return [];
    return [["MEETS", g.meets], ["WHERE", g.where], ["PACE", g.pace],
            ["AFTER THE RUN", g.after], ["SIZE", g.size], ["FOUNDED", g.founded]].filter(([, v]) => v);
  }, [data]);

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-5">
        <p className="font-display text-4xl">CLUB NOT FOUND.</p>
        <Link to="/clubs" className="font-mono-label link-underline text-white/60">BACK TO THE DIRECTORY</Link>
      </div>
    );
  }
  if (!data) return <p className="font-mono-label text-white/40 py-32 text-center">LOADING...</p>;

  const { club, related, city_page } = data;
  const mapsUrl = `https://www.google.com/maps/search/${encodeURIComponent(`${club.name} ${club.city || ""} ${club.state || ""}`)}`;

  return (
    <div className="bg-night min-h-screen">
      <header className="px-5 lg:px-10 pt-10 pb-12 border-b border-white/10 bg-ink">
        <Breadcrumbs
          light
          items={[
            ["Home", "/"],
            ["Run Clubs", "/clubs"],
            ...(city_page ? [[`${club.city}${club.state ? `, ${club.state}` : ""}`, city_page.path]] : []),
            [club.name, `/clubs/${club.slug}`],
          ]}
        />
        <Link to="/clubs" data-testid="club-back-link" className="mt-5 inline-flex items-center gap-2 font-mono-label text-white/40 hover:text-white transition-colors duration-200">
          <ArrowLeft size={13} /> DIRECTORY
        </Link>
        <Reveal>
          <div className="flex flex-wrap items-center gap-5 mt-8">
            {club.logo && (
              <img
                data-testid="club-logo"
                src={club.logo.src}
                alt={club.logo.alt || `${club.name} logo`}
                width="96" height="96"
                className="h-20 w-20 sm:h-24 sm:w-24 rounded-full border border-white/15 object-cover shrink-0"
                loading="eager"
                fetchPriority="high"
              />
            )}
            <h1 data-testid="club-name" className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none">{club.name}</h1>
            {club.verified && (
              <span data-testid="club-verified-badge" className="flex items-center gap-1.5 font-mono-label border border-white/30 px-3 py-1.5 text-white/80">
                <BadgeCheck size={13} /> VERIFIED
              </span>
            )}
          </div>
          <p className="font-mono-label text-white/40 mt-4">
            {club.is_national ? "NATIONAL / MULTI-CITY ORGANIZATION" : `${club.city}, ${club.state}`}
            {club.neighborhood ? ` · ${club.neighborhood}` : ""}
          </p>
          <p className="text-white/60 max-w-2xl mt-6">{club.description}</p>
          <div className="flex flex-wrap gap-2 mt-6">
            {(club.tags || []).map((t) => (
              <Link key={t} to={`/clubs?tag=${encodeURIComponent(t)}`} className="font-mono-label border border-white/20 px-3 py-1.5 text-white/60 hover:border-white hover:text-white transition-colors duration-200">
                {t}
              </Link>
            ))}
          </div>
        </Reveal>

        <div className="flex flex-wrap gap-3 mt-10">
          <SaveClubButton
            slug={club.slug}
            label
            size={14}
            className="btn-invert flex items-center gap-2 border border-white/40 font-mono-label px-6 py-3.5 hover:bg-white hover:text-ink"
          />
          <CheckinButton
            slug={club.slug}
            clubName={club.name}
            className="btn-invert flex items-center gap-2 border border-white/40 font-mono-label px-6 py-3.5 hover:bg-white hover:text-ink"
          />
          {(club.website || club.instagram) && (
            <a
              href={club.website || `https://instagram.com/${club.instagram}`}
              target="_blank" rel="noopener noreferrer"
              data-testid="club-run-with-cta"
              className="btn-invert bg-white text-ink font-mono-label px-6 py-3.5 border border-white hover:bg-transparent hover:text-white"
            >
              RUN WITH THIS CLUB
            </a>
          )}
          {club.instagram && (
            <a href={`https://instagram.com/${club.instagram}`} target="_blank" rel="noopener noreferrer" data-testid="club-instagram-cta" className="btn-invert flex items-center gap-2 border border-white/40 font-mono-label px-6 py-3.5 hover:bg-white hover:text-ink">
              <Instagram size={14} /> @{club.instagram}
            </a>
          )}
          {club.website && (
            <a href={club.website} target="_blank" rel="noopener noreferrer" data-testid="club-website-cta" className="btn-invert flex items-center gap-2 border border-white/40 font-mono-label px-6 py-3.5 hover:bg-white hover:text-ink">
              <Globe size={14} /> WEBSITE
            </a>
          )}
          {(club.links || []).map((l) => (
            <a
              key={l.href}
              href={l.href}
              target="_blank" rel="noopener noreferrer"
              data-testid={`club-link-cta-${(l.label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="btn-invert flex items-center gap-2 border border-white/40 font-mono-label px-6 py-3.5 hover:bg-white hover:text-ink"
            >
              <ExternalLink size={14} /> {l.label}
            </a>
          ))}
          {!club.is_national && (
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="club-directions-cta" className="btn-invert flex items-center gap-2 border border-white/40 font-mono-label px-6 py-3.5 hover:bg-white hover:text-ink">
              <MapPin size={14} /> GET DIRECTIONS
            </a>
          )}
        </div>
      </header>

      {club.media && (
        <section data-testid="club-media-section" className="border-b border-white/10 px-5 lg:px-10 py-14">
          <Reveal>
            <p className="font-mono-label text-white/40">FROM THE STREETS</p>
            <h2 className="font-display text-3xl lg:text-4xl mt-3">THIS IS WHAT SHOWING UP LOOKS LIKE.</h2>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-px bg-white/10 border border-white/10 mt-8">
            <div className="lg:col-span-3 bg-ink">
              <img
                data-testid="club-media-photo"
                src={club.media.photo}
                srcSet={club.media.photo_small ? `${club.media.photo_small} 800w, ${club.media.photo} 1600w` : undefined}
                sizes="(max-width: 1024px) 100vw, 60vw"
                alt={club.media.photo_alt || `${club.name} community`}
                className="img-bw h-full w-full object-cover"
                loading="lazy"
              />
            </div>
            <VideoTile
              testid="club-media-video"
              src={club.media.video}
              srcMobile={club.media.video_mobile}
              poster={club.media.poster}
              caption={club.media.caption}
              className="lg:col-span-2 aspect-video lg:aspect-auto"
            />
          </div>
        </section>
      )}

      {club.guide && (
        <section data-testid="club-guide-section" className="light-section bg-paper text-ink border-b border-white/10">
          <div className="px-5 lg:px-10 py-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-3">
              <h2 className="font-mono-label text-ink/40">NRC RUN CLUB SCORE</h2>
              <p data-testid="club-guide-score" className="font-display text-6xl lg:text-7xl mt-3 leading-none">
                {club.guide.score}<span className="text-2xl text-ink/40">/100</span>
              </p>
              <p className="font-mono-label mt-3 border border-ink px-3 py-1.5 inline-block">{club.guide.grade?.toUpperCase()}</p>
              <div className="flex flex-wrap gap-1.5 mt-5">
                {(club.guide.chips || []).map((ch) => (
                  <span key={ch} className="font-mono-label border border-ink/25 px-2.5 py-1 text-ink/70">{ch}</span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-9">
              <p className="text-ink text-lg lg:text-xl leading-relaxed max-w-3xl">{club.guide.verdict}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10 mt-8">
                {guideFacts.map(([k, v]) => (
                    <div key={k} className="bg-paper p-5">
                      <p className="font-mono-label text-ink/40">{k}</p>
                      <p className="text-sm text-ink/80 mt-2">{v}</p>
                    </div>
                  ))}
              </div>
              {club.guide.warning && (
                <div data-testid="club-guide-warning" className="border-l-2 border-ink bg-ink/5 p-5 mt-6">
                  <p className="font-mono-label text-ink/50 mb-2">KNOW BEFORE YOU GO</p>
                  <p className="text-sm text-ink/80">{club.guide.warning}</p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                {club.guide.best_for && (
                  <div>
                    <p className="font-mono-label text-ink/40 mb-2">BEST FOR</p>
                    <p className="text-sm text-ink/75">{club.guide.best_for}</p>
                  </div>
                )}
                {club.guide.first_timer && (
                  <div>
                    <p className="font-mono-label text-ink/40 mb-2">NEVER DONE THIS BEFORE?</p>
                    <p className="text-sm text-ink/75">{club.guide.first_timer}</p>
                  </div>
                )}
              </div>
              {club.guide.notable && (
                <p className="text-sm text-ink/70 border border-ink/15 p-5 mt-8">{club.guide.notable}</p>
              )}
              <p className="font-mono-label text-ink/35 mt-8">
                SCORED IN THE{" "}
                <a href="https://ninjathlete.com/blogs/article/best-running-clubs-usa-city-guide" target="_blank" rel="noopener noreferrer" className="link-underline text-ink/60">
                  BEST RUN CLUBS IN AMERICA 2026 GUIDE
                </a>
                {" "}· PACE HONESTY, NO-DROP MECHANICS, FIRST-TIMER EXPERIENCE, RELIABILITY, ETIQUETTE, CULTURE, ACCESS, SUBSTANCE
              </p>
            </div>
          </div>
        </section>
      )}

      <section className="px-5 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/10 border-b border-white/10">
        <div className="bg-night p-8">
          <h2 className="font-mono-label text-white/40">MEETING TIMES & SCHEDULE</h2>
          {(club.meet_days || []).length > 0 && (
            <p data-testid="club-meet-days" className="font-display text-xl mt-3">
              {club.meet_days.map((d) => d.toUpperCase()).join(" · ")}
            </p>
          )}
          <p data-testid="club-schedule-note" className="text-white/70 text-sm mt-3">{club.schedule_note}</p>
        </div>
        <div className="bg-night p-8">
          <h2 className="font-mono-label text-white/40">VERIFICATION</h2>
          <p className="text-white/70 text-sm mt-3">
            {club.last_verified ? `Sources last checked ${club.last_verified}.` : "Not yet verified. Details compiled from public research."}
          </p>
          {club.source_url && (
            <a href={club.source_url} target="_blank" rel="noopener noreferrer" className="font-mono-label link-underline text-white/50 mt-3 inline-block">SOURCE</a>
          )}
        </div>
        <div className="bg-night p-8">
          <h2 className="font-mono-label text-white/40">CLAIM STATUS</h2>
          <p className="text-white/70 text-sm mt-3"><span className="capitalize">{club.claim_status}</span>. Run this club? Claim the profile to manage details, events, and merch collabs.</p>
          <Link to={`/submit?tab=claim&club=${club.slug}`} data-testid="club-claim-cta" className="font-mono-label link-underline text-white/50 mt-3 inline-block">CLAIM THIS CLUB</Link>
        </div>
      </section>

      <div className="px-5 lg:px-10 py-8 border-b border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p data-testid="club-accuracy-notice" className="text-white/40 text-sm flex items-center gap-2">
          <Flag size={13} /> Schedules change. Confirm the latest details with the club before you go.
        </p>
        <Link to={`/submit?tab=correct&club=${club.slug}`} data-testid="club-correction-link" className="font-mono-label link-underline text-white/50">
          SUGGEST A CORRECTION
        </Link>
      </div>

      <section data-testid="club-first-visit" className="px-5 lg:px-10 py-14 border-b border-white/10">
        <h2 className="font-display text-3xl lg:text-4xl">PLAN YOUR FIRST VISIT.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-8">
          <div className="bg-night p-6">
            <h3 className="font-mono-label text-white/40">WHEN TO SHOW UP</h3>
            <p className="text-white/70 text-sm mt-3">
              {(club.meet_days || []).length > 0
                ? `${club.name} runs on ${club.meet_days.join(", ")}. ${club.schedule_note || ""}`
                : club.schedule_note || `We have not verified a public schedule for ${club.name} yet — check their ${club.instagram ? "Instagram" : "website or socials"} for this week's run.`}
              {" "}Arrive ten minutes early so you can find the group and get placed in a pace group.
            </p>
          </div>
          <div className="bg-night p-6">
            <h3 className="font-mono-label text-white/40">WHAT TO EXPECT</h3>
            <p className="text-white/70 text-sm mt-3">
              {(club.tags || []).length > 0
                ? `This club is tagged ${club.tags.slice(0, 3).join(", ").toLowerCase()}.`
                : "Community run club — expect mixed paces and a social finish."}
              {" "}
              {club.verified
                ? `Details were last human-verified${club.last_verified ? ` on ${club.last_verified}` : ""} against public sources.`
                : "Listing details come from public research and have not been claimed by the club yet."}
            </p>
          </div>
          <div className="bg-night p-6">
            <h3 className="font-mono-label text-white/40">BEFORE YOU GO</h3>
            <p className="text-white/70 text-sm mt-3">
              Schedules shift with seasons — confirm this week's meetup on{" "}
              {club.instagram ? (
                <a href={`https://instagram.com/${club.instagram}`} target="_blank" rel="noopener noreferrer" className="link-underline text-white/90">@{club.instagram}</a>
              ) : ("the club's own channels")}{" "}
              before heading out. First run club ever? The checklist below covers the nine questions worth asking.
            </p>
          </div>
        </div>
        <div className="mt-8">
          <ChecklistMagnet source={`checklist-club-${club.slug}`} />
        </div>
      </section>

      {(club.faqs || []).length > 0 && (
        <section data-testid="club-faq-section" className="px-5 lg:px-10 py-14 border-b border-white/10">
          <h2 className="font-display text-3xl lg:text-4xl">QUESTIONS, ANSWERED.</h2>
          <div className="mt-8 max-w-3xl space-y-px">
            {club.faqs.map((f) => (
              <details key={f.q} className="border border-white/10 p-5 group" data-testid="club-faq-item">
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                  <h3 className="font-display text-lg leading-snug">{f.q}</h3>
                  <span aria-hidden="true" className="text-white/30 shrink-0 transition-transform duration-200 group-open:rotate-45">+</span>
                </summary>
                <p className="text-white/70 text-sm mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {related.length > 0 && (
        <section className="px-5 lg:px-10 py-16">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <h2 className="font-mono-label text-white/40">MORE IN {club.city ? club.city.toUpperCase() : "THE DIRECTORY"}</h2>
            {city_page && (
              <Link to={city_page.path} data-testid="club-city-hub-link" className="font-mono-label link-underline text-white/60">
                ALL {city_page.name.toUpperCase()} RUN CLUBS →
              </Link>
            )}
          </div>
          {related.map((c, i) => (
            <ClubRow key={c.slug} club={c} index={i} />
          ))}
        </section>
      )}
    </div>
  );
}
